/**
 * Brings the site's content under the CMS.
 *
 * Every page here reads its content from a module in `src/data`. Rewriting all
 * nineteen of those consumers to fetch for themselves would have meant nineteen
 * loading states on a marketing site that has no use for them, so instead this
 * fetches the public feeds once at startup and merges what the CMS owns into
 * those modules before the first render.
 *
 * The merge is deliberately partial. The CMS holds titles, authorship, ordering,
 * placement, publish state, page copy and destinations; it does not hold article
 * prose or photography, and it has no upload pipeline yet. So anything the CMS
 * does not own keeps its bundled value, and a media field is only adopted when
 * it looks like a real path rather than the placeholder filename a record
 * carries by default.
 *
 * If the API is unreachable the site renders exactly as it did before. A
 * marketing page showing slightly stale copy beats one showing nothing.
 */

import { api } from './api';
import { embedUrl } from './embedUrl';
import { FOCUS_AREAS as MENU_FOCUS, INNOVATIONS as MENU_INNOVATIONS, SITE_LINKS, SOCIAL_URLS } from '@/data/navigation';
import { FOCUS_AREA_PAGES, INNOVATION_PAGES, type InnovationPage } from '@/data/pages';
import { CARD_CTA, HOME_INNOVATIONS, nameParts, type InnovationCard } from '@/data/innovations';
import { blankFocusContent, FOCUS_CONTENT, type FocusAttachments, type FocusContent } from '@/data/focusContent';
import { HERO_PROJECTS, PROJECTS, type Project, type ProjectStatus } from '@/data/projects';
import {
    ARTICLES,
    DOC_COLLECTIONS,
    DOC_RECENT,
    DOC_TOP,
    DOCUMENTS,
    emptyArticle,
    GALLERY,
    RECENT_RAIL,
    RECENT_STORIES,
    STORIES,
    TOP_ARTICLE,
    TOP_STORY,
    type DocCollection,
} from '@/data/resources';
import { CONTACT, FAQS, HELP_INTRO_REF, TUTORIALS } from '@/data/help';
import { HOME, LEGAL_AID_FORM, type Solution } from '@/data/home';
import { JDI, type JdiRow } from '@/data/jdi';
import { SOCIAL_POSTS, type SocialNetwork, type SocialPost } from '@/data/social';
import { nameParts as teamNameParts, TEAM, TEAM_GROUPS, type TeamGroup, type TeamMember } from '@/data/team';
import { initialsOf as quoteInitials, TESTIMONIALS, type TestimonialItem } from '@/data/testimonials';
import { PARTNERS, siteHref, type PartnerLogo } from '@/data/partners';
import { ABOUT, type TextRun } from '@/data/about';
import { PUBLICATIONS as REPORT_PUBLICATIONS, type Publication } from '@components/landing/Report';
import type { ArticleItem, DownloadItem, GalleryItem } from '@components/pages/ResourceSections';
import { productMark } from '@/data/productMarks';

export type Doc = Record<string, unknown>;

/* ── Readers ──────────────────────────────────────────────────────────── */

const str = (value: unknown, fallback = ''): string => (typeof value === 'string' && value.trim() ? value : fallback);

const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const docs = (value: unknown): Doc[] => (Array.isArray(value) ? (value as Doc[]) : []);

const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && !!v.trim()) : [];

const obj = (value: unknown): Doc => (value && typeof value === 'object' ? (value as Doc) : {});

/**
 * Media fields hold a bare filename until there is somewhere to upload to, and a
 * bare filename resolves to nothing. Only a real path replaces bundled artwork.
 */
const media = (value: unknown, fallback: string): string => {
    const path = typeof value === 'string' ? value.trim() : '';
    return /^(\/|https?:|data:)/.test(path) ? path : fallback;
};

/** CMS long-form fields are one string; the site renders paragraph arrays. */
export const paragraphs = (value: unknown, fallback: string[]): string[] => {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return fallback;
    const split = text
        .split(/\n\s*\n|\n/)
        .map((p) => p.trim())
        .filter(Boolean);
    return split.length ? split : fallback;
};

/**
 * Replaces a list in place, keeping the existing element objects where it can.
 * Derived arrays elsewhere (`RECENT_ARTICLES`, `TOP_STORY`) hold references into
 * these lists, so mutating the elements keeps those in agreement; replacing the
 * objects outright would silently strand them.
 *
 * An empty feed is left alone. A collection nobody has published yet should show
 * the bundled sample, not an empty page.
 */
/**
 * Replaces a pool with exactly what the CMS sent.
 *
 * Unlike `reconcile`, there is no bundled row underneath to borrow a missing
 * field from, and an empty list empties the pool. Pools move onto this as they
 * are cut over to the API as their only source.
 */
function fill<T>(target: T[], live: Doc[], build: (doc: Doc) => T): void {
    target.splice(0, target.length, ...live.map(build));
}

function reconcile<T extends object>(
    target: T[],
    live: Doc[],
    build: (doc: Doc, fallback: T, index: number) => T,
): void {
    const base = target.slice();
    // An empty list from a feed that answered is an answer: the editor removed
    // everything, or published nothing yet. Leaving the bundled stand-ins would
    // show invented rows as real work — the same way a re-run seed used to put
    // back what someone had deleted.
    if (!live.length) {
        target.length = 0;
        return;
    }
    if (!base.length) return;

    const next = live.map((doc, i) => build(doc, base[i % base.length], i));
    next.forEach((value, i) => {
        if (target[i]) Object.assign(target[i], value);
        else target[i] = value;
    });
    target.length = next.length;
}

/**
 * Like `reconcile`, but each live record takes its bundled counterpart by key
 * (a slug) rather than by position — so a reordered menu keeps every item's
 * own icon, and a record the bundle never had gets the first item as a stand-in
 * for whatever the CMS cannot supply yet (a logo, until uploads arrive).
 */
function reconcileBy<T extends object>(
    target: T[],
    live: Doc[],
    keyOfLive: (doc: Doc) => string,
    keyOfTarget: (item: T) => string,
    build: (doc: Doc, fallback: T) => T,
): void {
    const base = target.slice();
    if (!live.length) {
        target.length = 0;
        return;
    }
    if (!base.length) return;
    const byKey = new Map(base.map((item) => [keyOfTarget(item), item]));

    const next = live.map((doc) => build(doc, byKey.get(keyOfLive(doc)) ?? base[0]));
    next.forEach((value, i) => {
        if (target[i]) Object.assign(target[i], value);
        else target[i] = value;
    });
    target.length = next.length;
}

/** The last segment of a CMS destination or slug — what the site routes on. */
const lastSegment = (value: unknown): string => str(value).split('/').filter(Boolean).pop() ?? '';

/* ── Builders, one per content type ───────────────────────────────────── */

/**
 * A CMS article. Imported Medium posts carry the whole piece — sanitised HTML,
 * plain paragraphs, date, reading time, tags and the canonical Medium link —
 * so nothing is borrowed from a bundled sample once the CMS has answered.
 */
export const articleFrom = (doc: Doc): ArticleItem => {
    const paragraphs = strings(doc.body);
    return {
        // The CMS slug carries its route prefix; the site routes on the last segment.
        slug: str(doc.slug).split('/').filter(Boolean).pop() ?? '',
        title: str(doc.title),
        author: str(doc.author),
        image: media(doc.image, ''),
        avatar: media(doc.avatar, ''),
        excerpt: str(doc.excerpt),
        quote: str(doc.quote),
        // The feed leaves bodies out by design; the article page fetches the record.
        body: paragraphs.length ? paragraphs : undefined,
        bodyHtml: str(doc.bodyHtml) || undefined,
        date: str(doc.publishDate) || undefined,
        publishedAt: str(doc.publishedAt) || undefined,
        searchText: str(doc.searchText) || undefined,
        readingMinutes: num(doc.readingMinutes) || undefined,
        tags: strings(doc.tags),
        originalUrl: str(doc.mediumUrl) || undefined,
    };
};

/**
 * A CMS story. The imported gavel.ng stories carry their standfirst, a byline
 * avatar and an absolute image URL on another host, so the bundled artwork is
 * kept as `fallbackImage` in case that host does not answer.
 */
export const storyFrom = (doc: Doc): ArticleItem => {
    const article = str(doc.article);
    return {
        slug: str(doc.id),
        title: str(doc.text),
        author: str(doc.author),
        image: media(doc.image, ''),
        avatar: media(doc.avatar, ''),
        excerpt: str(doc.excerpt) || (article ? article.slice(0, 180) : ''),
        // The CMS has no pull quote for a story, and a bundled one would misattribute.
        quote: '',
        body: article ? paragraphs(article, []) : undefined,
    };
};

const galleryFrom = (doc: Doc, fallback: GalleryItem): GalleryItem => {
    const kind = doc.kind === 'image' ? 'image' : 'video';
    return {
        slug: str(doc.id, fallback.slug),
        kind,
        title: str(doc.title, fallback.title),
        caption: str(doc.alt),
        image: media(doc.media, fallback.image),
        // Editors paste the watch link; the player needs the embed form.
        videoUrl: kind === 'video' ? embedUrl(str(doc.link)) : undefined,
        duration: kind === 'video' ? str(doc.duration) || undefined : undefined,
        featured: doc.featured === true,
    };
};

/** Publications store their title in halves for the two-tone landing showcase. */
const docTitle = (doc: Doc, fallback: string): string =>
    [str(doc.titleDark), str(doc.titleGrey)].filter(Boolean).join(' ').trim() || fallback;

const downloadFrom = (doc: Doc): DownloadItem => ({
    text: docTitle(doc, ''),
    cover: media(doc.cover, DOC_PLACEHOLDER_COVER),
    slug: str(doc.id),
    file: media(doc.file, ''),
});

/** Generic cover art for a document without one; never a sample article's photograph. */
const DOC_PLACEHOLDER_COVER = '/landing/report/thumb-1.webp';
/** The organisation's mark, for a document nobody is credited on. */
const GAVEL_MARK = '/landing/icons/logo.svg';

/**
 * The Top/Recent split renders documents through the article card. A document
 * opens its file rather than an article page, is credited to the first of its
 * team or to the organisation, and shows no standfirst unless it has one — a
 * real report must not carry a sample's byline or blurb.
 */
const docAsArticle = (doc: Doc): ArticleItem => {
    const team = strings(doc.team);
    return {
        slug: str(doc.id),
        title: docTitle(doc, ''),
        author: team[0] ?? "Citizens' Gavel",
        image: media(doc.cover, DOC_PLACEHOLDER_COVER),
        avatar: GAVEL_MARK,
        excerpt: str(doc.description),
        quote: '',
        body: [],
        href: media(doc.file, '') || undefined,
    };
};

/**
 * The landing showcase and the "Top Featured" block on the Publications page:
 * whichever publications hold a landing slot, in slot order. Metadata the CMS
 * has not been given (period, page count, authors) stays blank rather than
 * borrowing a bundled sample's — a real publication must not show a made-up
 * date or team. Team members have names only until there is somewhere to
 * upload a portrait.
 */
const showcaseFrom = (doc: Doc, fallback: Publication): Publication => {
    const cover = media(doc.cover, fallback.cover);
    return {
        titleDark: str(doc.titleDark, fallback.titleDark),
        titleGrey: str(doc.titleGrey),
        paragraph: str(doc.description),
        cover,
        thumb: cover,
        published: str(doc.published),
        pages: str(doc.pages),
        format: str(doc.format),
        language: str(doc.language),
        team: strings(doc.team).map((name) => ({ name })),
    };
};

function mergeShowcase(items: Doc[]): void {
    const featured = items
        .filter((d) => num(d.landingSlot) > 0)
        .sort((a, b) => num(a.landingSlot) - num(b.landingSlot))
        .slice(0, REPORT_PUBLICATIONS.length);
    reconcile(REPORT_PUBLICATIONS, featured, showcaseFrom);
}

/* ── Merges ───────────────────────────────────────────────────────────── */

/** Namespaced ref (`story:s1`, `doc:reports:r-2`) to its index in a site pool. */
type RefIndex = Map<string, number>;

const indexBy = (live: Doc[], prefix: string, into: RefIndex): void => {
    live.forEach((doc, i) => into.set(`${prefix}${str(doc.id)}`, i));
};

function mergeNavigation(nav: Doc): void {
    const areas = docs(nav.focusAreas);
    const innovations = docs(nav.innovations);

    reconcileBy(
        MENU_FOCUS,
        areas,
        (doc) => str(doc.slug),
        (item) => lastSegment(item.href),
        (doc, fallback) => ({
            label: [str(doc.titleLead), str(doc.titleBold)].filter(Boolean).join(' ').trim() || fallback.label,
            description: str(doc.menuDescription, fallback.description),
            icon: media(doc.icon, fallback.icon),
            href: `/focus-areas/${str(doc.slug, lastSegment(fallback.href))}`,
            homeSlot: num(doc.homeSlot),
        }),
    );

    reconcileBy(
        MENU_INNOVATIONS,
        innovations,
        (doc) => lastSegment(doc.destination) || str(doc.id),
        (item) => lastSegment(item.href),
        (doc, fallback) => ({
            label: str(doc.name, fallback.label),
            description: str(doc.summary, fallback.description),
            icon: media(doc.logo, fallback.icon),
            // The destination the CMS sets is where the menu item goes.
            href: str(doc.destination, fallback.href),
        }),
    );

    for (const link of docs(obj(nav.settings).social)) {
        const network = str(link.network);
        if (network in SOCIAL_URLS) SOCIAL_URLS[network] = str(link.url, SOCIAL_URLS[network]);
    }
    // The CMS decides the Donate button outright: an empty value hides it.
    if (typeof obj(nav.settings).donateUrl === 'string')
        SITE_LINKS.donate = (obj(nav.settings).donateUrl as string).trim();
}

/** `doc:reports:r1` → the report at that index of the reports pool, once hydrated. */
const attachedDocuments = (list: unknown): FocusAttachments['documents'] => {
    const out: FocusAttachments['documents'] = { publications: [], reports: [], infographics: [] };
    for (const ref of strings(list)) {
        const collection = ref.split(':')[1] as DocCollection;
        const index = refs.documents.get(ref);
        const item = index !== undefined && collection in out ? DOCUMENTS[collection][index] : undefined;
        if (item && !out[collection].includes(item)) out[collection].push(item);
    }
    return out;
};

const attachedFrom = <T>(list: unknown, index: RefIndex, pool: T[]): T[] =>
    strings(list)
        .map((ref) => index.get(ref))
        .filter((i): i is number => i !== undefined)
        .map((i) => pool[i])
        .filter((item, i, all): item is T => item !== undefined && all.indexOf(item) === i);

/**
 * The Focus Area pages. The CMS list is authoritative once it has answered: a
 * page it publishes exists (added if the bundle never had it), one it has
 * withdrawn is gone, and what each page shows — objectives, walkthrough,
 * attached stories, articles, gallery items and documents — is what an editor
 * attached, resolved against the pools hydrated above.
 */
function mergeFocusAreas(areas: Doc[]): void {
    if (!areas.length) return;
    const live = new Set<string>();

    for (const area of areas) {
        const slug = str(area.slug);
        if (!slug) continue;
        live.add(slug);

        let page = FOCUS_AREA_PAGES.find((p) => p.slug === slug);
        if (!page) {
            page = { slug, titleLead: '', titleBold: '', intro: '', statementDark: '', statementGrey: '' };
            FOCUS_AREA_PAGES.push(page);
        }
        const statement = obj(area.statement);
        Object.assign(page, {
            titleLead: str(area.titleLead, page.titleLead),
            titleBold: str(area.titleBold, page.titleBold),
            subtitle: str(area.subtitle, page.subtitle ?? '') || undefined,
            intro: str(area.intro, page.intro),
            statementDark: str(statement.dark, page.statementDark),
            statementGrey: str(statement.grey, page.statementGrey),
            heroImage: media(area.heroImage, page.heroImage ?? '') || undefined,
        });

        const content: FocusContent = (FOCUS_CONTENT[slug] ??= blankFocusContent());

        const objectives = docs(area.objectives)
            .map((o) => ({ title: str(o.title), body: str(o.body) }))
            .filter((o) => o.title || o.body);
        if (objectives.length) content.objectives = objectives;

        const video = obj(area.video);
        content.video = {
            heading: str(video.heading, content.video.heading),
            copy: str(video.copy, content.video.copy),
            thumbnail: media(video.thumbnail, content.video.thumbnail),
            url: str(video.url, content.video.url ?? '') || undefined,
        };

        const related = obj(area.related);
        content.attached = {
            stories: attachedFrom(related.stories, refs.stories, STORIES),
            articles: attachedFrom(related.articles, refs.articles, ARTICLES),
            gallery: attachedFrom(related.gallery, refs.gallery, GALLERY),
            documents: attachedDocuments(related.documents),
        };
    }

    // A page the CMS no longer publishes should not answer its old address.
    for (let i = FOCUS_AREA_PAGES.length - 1; i >= 0; i--) {
        if (!live.has(FOCUS_AREA_PAGES[i].slug)) FOCUS_AREA_PAGES.splice(i, 1);
    }
}

/**
 * The products. The homepage stack is whatever holds a slot, in slot order;
 * the product pages are the Live products — a bundled page keeps its designed
 * headline, a product the bundle never had is titled by its name — and a
 * withdrawn product's page is gone. Logos stay bundled until uploads arrive:
 * the CMS record carries a bare filename, and the site keeps the mark it ships
 * with for that product.
 */
function mergeInnovations(items: Doc[]): void {
    if (!items.length) return;
    const slugOf = (doc: Doc): string => lastSegment(doc.destination) || str(doc.id);
    const bundledPage = new Map(INNOVATION_PAGES.map((p) => [p.slug, p]));
    const bundledLogo = (slug: string): string => productMark(slug) || (MENU_INNOVATIONS[0]?.icon ?? '');

    const onHome = items
        .filter((doc) => num(doc.homeSlot) > 0)
        .sort((a, b) => num(a.homeSlot) - num(b.homeSlot))
        .slice(0, 4);
    fill(HOME_INNOVATIONS, onHome, (doc): InnovationCard => {
        const slug = slugOf(doc);
        return {
            slug,
            ...nameParts(str(doc.name)),
            logo: media(doc.logo, bundledLogo(slug)),
            href: str(doc.destination),
            cta: CARD_CTA[slug] ?? 'Learn More',
            paragraph: str(doc.paragraph, str(doc.summary)),
        };
    });

    const pages: InnovationPage[] = items.map((doc) => {
        const slug = slugOf(doc);
        const page = bundledPage.get(slug);
        const name = str(doc.name, page?.label ?? slug);
        return {
            slug,
            titleLead: page?.titleLead ?? '',
            titleBold: page?.titleBold ?? name,
            label: name,
            intro: str(doc.summary, page?.intro ?? ''),
            logo: media(doc.logo, bundledLogo(slug)),
        };
    });
    INNOVATION_PAGES.splice(0, INNOVATION_PAGES.length, ...pages);
}

const PROJECT_STATUS: ProjectStatus[] = ['Active', 'Pilot', 'Completed'];

/**
 * The projects. The CMS list is authoritative once it has answered: every
 * published project is here (a bundled one keeps its product mark and its
 * copy where the CMS has none), a drafted one is gone, and what a detail page
 * shows underneath is what an editor attached. The hero rail is derived from
 * the merged list, so it follows the CMS ordering.
 */
function mergeProjects(items: Doc[]): void {
    const slugOf = (doc: Doc): string => str(doc.slug, str(doc.id));
    // The CMS has no logo field on a project; the mark is a brand asset.
    const bundledLogo = productMark;

    fill(PROJECTS, items, (doc): Project => {
        const slug = slugOf(doc);
        const related = obj(doc.related);
        const status = str(doc.status) as ProjectStatus;
        return {
            slug,
            name: str(doc.name),
            tagline: str(doc.tagline),
            programs: strings(doc.programs),
            status: PROJECT_STATUS.includes(status) ? status : 'Active',
            heroSlot: num(doc.heroSlot),
            programSlots: obj(doc.programSlots) as Record<string, number>,
            image: media(doc.image, ''),
            logo: media(doc.logo, bundledLogo(slug)),
            website: str(doc.website) || undefined,
            started: str(doc.started),
            coverage: str(doc.coverage),
            stats: docs(doc.stats).map((stat) => ({ value: str(stat.value), label: str(stat.label) })),
            problem: paragraphs(doc.problem, []),
            solution: paragraphs(doc.solution, []),
            objectives: strings(doc.objectives),
            attached: {
                stories: attachedFrom(related.stories, refs.stories, STORIES),
                articles: attachedFrom(related.articles, refs.articles, ARTICLES),
                gallery: attachedFrom(related.gallery, refs.gallery, GALLERY),
                documents: attachedDocuments(related.documents),
            },
        };
    });

    const hero = PROJECTS.filter((p) => p.heroSlot > 0).sort((a, b) => a.heroSlot - b.heroSlot);
    HERO_PROJECTS.splice(0, HERO_PROJECTS.length, ...hero);
}

/**
 * The team. The CMS list replaces the bundled sample outright — real people,
 * in the order and groups the CMS keeps. A member without a portrait yet shows
 * their initials; without a bio, just their name and role.
 */
function mergeTeam(items: Doc[]): void {
    if (!items.length) return;
    const members: TeamMember[] = items.map((doc) => {
        const name = str(doc.name);
        const group = str(doc.group) as TeamGroup;
        return {
            ...teamNameParts(name),
            name,
            role: str(doc.role),
            photo: media(doc.photo, ''),
            bio: paragraphs(doc.bio, []),
            group: TEAM_GROUPS.includes(group) ? group : 'Team Members',
        };
    });
    TEAM.splice(0, TEAM.length, ...members);
}

/**
 * Testimonials and partners replace their bundled samples outright — even with
 * an empty list. The samples are draft copy and placeholder brands, and once
 * the CMS has answered they must never show; an empty list hides the section.
 */
function mergeTestimonials(items: Doc[]): void {
    const quotes: TestimonialItem[] = items.map((doc) => {
        const name = str(doc.attributed);
        return {
            quote: str(doc.quote),
            name,
            role: str(doc.place),
            avatar: media(doc.avatar, ''),
            initials: str(doc.initials, quoteInitials(name)),
        };
    });
    TESTIMONIALS.splice(0, TESTIMONIALS.length, ...quotes);
}

function mergePartners(items: Doc[]): void {
    const logos: PartnerLogo[] = items
        .map((doc) => ({
            name: str(doc.name),
            logo: media(doc.logoLight, ''),
            href: siteHref(str(doc.site)),
        }))
        .filter((p) => p.logo);
    PARTNERS.splice(0, PARTNERS.length, ...logos);
}

function mergeHome(content: Doc): void {
    const hero = obj(content.hero);
    const words = strings(hero.words);
    Object.assign(HOME.hero, {
        words: words.length === HOME.hero.words.length ? words : HOME.hero.words,
        paragraph: str(hero.paragraph, HOME.hero.paragraph),
        statValue: str(hero.statValue, HOME.hero.statValue),
        statLabel: str(hero.statLabel, HOME.hero.statLabel),
        ctaLabel: str(hero.ctaLabel, HOME.hero.ctaLabel),
        ctaHref: str(hero.ctaHref, HOME.hero.ctaHref),
        railHeading: str(hero.railHeading, HOME.hero.railHeading),
        background: media(hero.background, HOME.hero.background),
        backgroundAlt: str(hero.backgroundAlt, HOME.hero.backgroundAlt),
    });

    // "Our Solutions": the CMS list replaces the bundled slides once it has any,
    // with each slide's attached stories resolved against the stories pool.
    const tackled = obj(content.tackled);
    HOME.tackled.heading = str(tackled.heading, HOME.tackled.heading);
    const solutions: Solution[] = docs(tackled.solutions)
        .map((doc, i) => ({
            num: str(doc.num, String(i + 1).padStart(2, '0')),
            headTop: str(doc.headTop),
            headBottom: str(doc.headBottom),
            paragraph: str(doc.paragraph),
            details: docs(doc.details)
                .map((d) => ({ title: str(d.title), body: str(d.body) }))
                .filter((d) => d.title || d.body),
            image: media(doc.image, ''),
            imageAlt: str(doc.imageAlt),
            stories: attachedFrom(doc.storyRefs, refs.stories, STORIES),
        }))
        .filter((s) => s.headTop || s.headBottom || s.paragraph);
    if (solutions.length) HOME.tackled.solutions = solutions;

    const metrics = obj(content.metrics);
    HOME.metrics.heading = str(metrics.heading, HOME.metrics.heading);
    const figures = docs(metrics.figures)
        .map((f) => ({ value: str(f.value), label: str(f.label) }))
        .filter((f) => f.value && f.label);
    if (figures.length) HOME.metrics.figures = figures;
    HOME.innovations.intro = str(obj(content.innovations).intro, HOME.innovations.intro);

    const video = obj(content.video);
    Object.assign(HOME.video, {
        heading: str(video.heading, HOME.video.heading),
        headingBold: str(video.headingBold, HOME.video.headingBold),
        paragraph: str(video.paragraph, HOME.video.paragraph),
        galleryCaption: str(video.galleryCaption, HOME.video.galleryCaption),
    });

    const legalAid = obj(content.legalAid);
    Object.assign(HOME.legalAid, {
        heading: str(legalAid.heading, HOME.legalAid.heading),
        paragraph: str(legalAid.paragraph, HOME.legalAid.paragraph),
        ctaLabel: str(legalAid.ctaLabel, HOME.legalAid.ctaLabel),
        ctaHref: str(legalAid.ctaHref, LEGAL_AID_FORM),
        stat: {
            value: str(obj(legalAid.stat).value, HOME.legalAid.stat.value),
            label: str(obj(legalAid.stat).label, HOME.legalAid.stat.label),
        },
        wideCard: promoCard(legalAid.wideCard, HOME.legalAid.wideCard),
        tallCard: promoCard(legalAid.tallCard, HOME.legalAid.tallCard),
    });

    for (const band of ['blog', 'justicePerformance', 'social'] as const) {
        const source = obj(content[band]);
        Object.assign(HOME[band], {
            titleLead: str(source.titleLead, HOME[band].titleLead),
            titleBold: str(source.titleBold, HOME[band].titleBold),
            blurb: str(source.blurb, HOME[band].blurb),
        });
    }
    const ranking = obj(content.justicePerformance);
    Object.assign(HOME.justicePerformance, {
        ctaLabel: str(ranking.ctaLabel, HOME.justicePerformance.ctaLabel),
        ctaHref: str(ranking.ctaHref, HOME.justicePerformance.ctaHref),
    });
}

/** A pull quote as the CMS stores it — runs of text, some grey — or nothing if it holds none. */
const runs = (value: unknown): TextRun[] =>
    docs(value)
        .map((r) => ({ text: str(r.text), grey: r.grey === true }))
        .filter((r) => r.text);

const promoCard = <T extends { image: string; imageAlt: string; title: string; body: string }>(
    value: unknown,
    fallback: T,
): T => {
    const card = obj(value);
    return {
        ...fallback,
        image: media(card.image, fallback.image),
        imageAlt: str(card.imageAlt, fallback.imageAlt),
        title: str(card.title, fallback.title),
        body: str(card.body, fallback.body),
    };
};

function mergeAbout(content: Doc): void {
    const hero = obj(content.hero);
    Object.assign(ABOUT.hero, {
        lead: str(hero.lead, ABOUT.hero.lead),
        body: str(hero.body, ABOUT.hero.body),
        ctaLabel: str(hero.ctaLabel, ABOUT.hero.ctaLabel),
        ctaHref: str(hero.ctaHref, ABOUT.hero.ctaHref),
        photo: media(hero.photo, ABOUT.hero.photo),
        photoAlt: str(hero.photoAlt, ABOUT.hero.photoAlt),
        background: media(hero.background, ABOUT.hero.background),
    });

    const who = obj(content.who);
    Object.assign(ABOUT.who, {
        heading: str(who.heading, ABOUT.who.heading),
        highlight: str(who.highlight, ABOUT.who.highlight),
        body1: str(who.body1, ABOUT.who.body1),
        body2: str(who.body2, ABOUT.who.body2),
        quote: runs(who.quote).length ? runs(who.quote) : ABOUT.who.quote,
        authorName: str(who.authorName, ABOUT.who.authorName),
        authorRole: str(who.authorRole, ABOUT.who.authorRole),
        authorAvatar: media(who.authorAvatar, ABOUT.who.authorAvatar),
    });

    const mv = obj(content.missionVision);
    if (strings(mv.missionParagraphs).length) ABOUT.missionVision.missionParagraphs = strings(mv.missionParagraphs);
    if (strings(mv.visionParagraphs).length) ABOUT.missionVision.visionParagraphs = strings(mv.visionParagraphs);
    Object.assign(ABOUT.missionVision, {
        missionImage: media(mv.missionImage, ABOUT.missionVision.missionImage),
        missionImageAlt: str(mv.missionImageAlt, ABOUT.missionVision.missionImageAlt),
        visionImage: media(mv.visionImage, ABOUT.missionVision.visionImage),
        visionImageAlt: str(mv.visionImageAlt, ABOUT.missionVision.visionImageAlt),
    });

    const programmes = obj(content.programmes);
    ABOUT.programmes.intro = str(programmes.intro, ABOUT.programmes.intro);
    const stats = docs(programmes.stats)
        .map((f) => ({ value: str(f.value), caption: str(f.caption) }))
        .filter((f) => f.value && f.caption);
    if (stats.length) ABOUT.programmes.stats = stats;
    // The pill is dropped when the CMS leaves it empty; the heading keeps its bundled words.
    if (typeof programmes.eyebrow === 'string') ABOUT.programmes.eyebrow = programmes.eyebrow.trim();
    ABOUT.programmes.titleLead = str(programmes.titleLead, ABOUT.programmes.titleLead);
    ABOUT.programmes.titleBold = str(programmes.titleBold, ABOUT.programmes.titleBold);
    const services = docs(programmes.services)
        .map((sv) => ({ icon: str(sv.icon), title: str(sv.title), body: str(sv.body) }))
        .filter((sv) => sv.title);
    if (services.length) ABOUT.programmes.services = services;
    ABOUT.programmes.image = media(programmes.image, ABOUT.programmes.image);
    ABOUT.programmes.imageAlt = str(programmes.imageAlt, ABOUT.programmes.imageAlt);

    ABOUT.team.intro = str(obj(content.team).intro, ABOUT.team.intro);

    const volunteers = obj(content.volunteers);
    Object.assign(ABOUT.volunteers, {
        titleLead: str(volunteers.titleLead, ABOUT.volunteers.titleLead),
        titleBold: str(volunteers.titleBold, ABOUT.volunteers.titleBold),
        lead: str(volunteers.lead, ABOUT.volunteers.lead),
        counterValue: str(volunteers.counterValue, ABOUT.volunteers.counterValue),
        counterLabel: str(volunteers.counterLabel, ABOUT.volunteers.counterLabel),
        wideCard: promoCard(volunteers.wideCard, ABOUT.volunteers.wideCard),
        tallCard: promoCard(volunteers.tallCard, ABOUT.volunteers.tallCard),
    });

    const newsletter = obj(content.newsletter);
    Object.assign(ABOUT.newsletter, {
        heading: str(newsletter.heading, ABOUT.newsletter.heading),
        body: str(newsletter.body, ABOUT.newsletter.body),
        subscriberCount: str(newsletter.subscriberCount, ABOUT.newsletter.subscriberCount),
    });

    ABOUT.partners.intro = str(obj(content.partners).intro, ABOUT.partners.intro);

    const form = obj(content.volunteerForm);
    Object.assign(ABOUT.volunteerForm, {
        heading: str(form.heading, ABOUT.volunteerForm.heading),
        blurb: str(form.blurb, ABOUT.volunteerForm.blurb),
        submitLabel: str(form.submitLabel, ABOUT.volunteerForm.submitLabel),
        privacyNote: str(form.privacyNote, ABOUT.volunteerForm.privacyNote),
        interestAreas: strings(form.interestAreas).length
            ? strings(form.interestAreas)
            : ABOUT.volunteerForm.interestAreas,
        askPhone: typeof form.askPhone === 'boolean' ? form.askPhone : ABOUT.volunteerForm.askPhone,
        askMessage: typeof form.askMessage === 'boolean' ? form.askMessage : ABOUT.volunteerForm.askMessage,
    });

    const feedback = obj(content.feedback);
    Object.assign(ABOUT.feedback, {
        heading: str(feedback.heading, ABOUT.feedback.heading),
        subheading: str(feedback.subheading, ABOUT.feedback.subheading),
        submitLabel: str(feedback.submitLabel, ABOUT.feedback.submitLabel),
    });
}

function mergeHelp(help: Doc): void {
    HELP_INTRO_REF.text = str(help.intro, HELP_INTRO_REF.text);

    const contact = obj(help.contact);
    Object.assign(CONTACT, {
        heading: str(contact.heading, CONTACT.heading),
        body: str(contact.body, CONTACT.body),
        phone: str(contact.phone, CONTACT.phone),
        phoneNote: str(contact.phoneNote, CONTACT.phoneNote),
        email: str(contact.email, CONTACT.email),
        emailNote: str(contact.emailNote, CONTACT.emailNote),
    });

    reconcile(FAQS, docs(help.faqs), (doc, fallback) => ({
        question: str(doc.question, fallback.question),
        answer: str(doc.answer, fallback.answer),
    }));

    reconcile(TUTORIALS, docs(help.tutorials), (doc, fallback) => ({
        slug: str(doc.id, fallback.slug),
        title: str(doc.title, fallback.title),
        duration: str(doc.duration, fallback.duration),
        thumbnail: media(doc.thumbnail, fallback.thumbnail),
        videoUrl: str(doc.videoUrl, fallback.videoUrl ?? '') || undefined,
    }));
}

/** The index: every published year, replacing the bundled 2022 wholesale. */
function mergeJdi(feed: Doc): void {
    const byYear = obj(feed.byYear);
    const years = Object.keys(byYear).filter((y) => /^\d{4}$/.test(y) && Array.isArray(byYear[y]));
    if (!years.length) return;

    const rowFrom = (doc: Doc): JdiRow => {
        const number = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
        const health = doc.health;
        return {
            state: str(doc.state),
            avgDays: number(doc.avgDays),
            civilDays: number(doc.civilDays),
            criminalDays: number(doc.criminalDays),
            rank: number(doc.rank),
            health: health === 'Good' || health === 'Fair' || health === 'Poor' ? health : null,
            description: str(doc.description),
        };
    };

    JDI.byYear = Object.fromEntries(
        years.map((y) => [
            y,
            docs(byYear[y])
                .map(rowFrom)
                .filter((r) => r.state),
        ]),
    );
    JDI.years = years.sort();
    JDI.year = str(feed.year, JDI.years[JDI.years.length - 1]);
}

/* ── The fetch ────────────────────────────────────────────────────────── */

interface Rail {
    items: Doc[];
    top: Doc | null;
    recent: Doc[];
}

const refs = {
    stories: new Map<string, number>(),
    articles: new Map<string, number>(),
    gallery: new Map<string, number>(),
    documents: new Map<string, number>(),
};

/**
 * Whether the CMS answered at all this session. Bands that would otherwise
 * show bundled sample art check it: once the CMS has spoken, an empty slot is
 * an empty slot, not a stock photograph.
 */
export const SITE_STATE = {
    cmsAnswered: false,
    /**
     * True until the feed has landed or failed — which is *not* the same as the
     * first-paint cap below. A page that routes on a record it cannot find yet,
     * like an article opened straight from a link, must wait for this rather
     * than conclude the record does not exist.
     */
    hydrating: true,
};

/** Never let one dead endpoint take the whole hydration down with it. */
/* ── Late arrivals ────────────────────────────────────────────────────── */

/**
 * First paint is capped (see `hydrateSiteContent`), so on a slow connection the
 * pages render from the bundled pools and the CMS answer lands a moment later.
 * Those pools are plain arrays read during render, so nothing re-reads them on
 * its own — the tree has to be told. `App` subscribes to this; without it a slow
 * load leaves sample content on screen for the rest of the visit.
 */
let contentVersion = 0;
const versionListeners = new Set<() => void>();

export const siteContentVersion = {
    get: (): number => contentVersion,
    subscribe: (onChange: () => void): (() => void) => {
        versionListeners.add(onChange);
        return () => {
            versionListeners.delete(onChange);
        };
    },
};

const publishContentVersion = (): void => {
    contentVersion += 1;
    for (const listener of versionListeners) listener();
};

const attempt = async <T>(path: string): Promise<T | null> => api.get<T>(path).catch(() => null);

interface Bootstrap {
    home?: { content: Doc } | null;
    about?: { content: Doc } | null;
    help?: Doc | null;
    navigation?: Doc | null;
    jdi?: Doc | null;
    projects?: { items: Doc[]; programmes: Doc[] } | null;
    team?: { items: Doc[] } | null;
    testimonials?: { items: Doc[] } | null;
    partners?: { items: Doc[] } | null;
    resources?: Record<string, Rail | null> | null;
}

/**
 * The feeds fetched one by one, as the site did before `/site/bootstrap` existed.
 * Kept so the order of a deploy cannot matter: a new site against an API that
 * predates the combined feed still fills in, just over more connections.
 */
async function eachFeed(): Promise<Bootstrap> {
    const collections = ['articles', 'stories', 'gallery', ...DOC_COLLECTIONS];
    const [home, about, help, navigation, jdi, projects, team, testimonials, partners, ...rails] = await Promise.all([
        attempt<{ content: Doc }>('/site/home'),
        attempt<{ content: Doc }>('/site/about'),
        attempt<Doc>('/site/help'),
        attempt<Doc>('/site/navigation'),
        attempt<Doc>('/site/jdi'),
        attempt<{ items: Doc[]; programmes: Doc[] }>('/site/projects'),
        attempt<{ items: Doc[] }>('/site/team'),
        attempt<{ items: Doc[] }>('/site/testimonials'),
        attempt<{ items: Doc[] }>('/site/partners'),
        ...collections.map((name) => attempt<Rail>(`/site/resources/${name}`)),
    ]);
    return {
        home,
        about,
        help,
        navigation,
        jdi,
        projects,
        team,
        testimonials,
        partners,
        resources: Object.fromEntries(collections.map((name, i) => [name, rails[i]])),
    };
}

/**
 * The pools whose bundled contents are placeholders rather than the
 * organisation's own material. Structure is deliberately left alone — the
 * menus, focus areas, projects, the index and the help centre keep the site's
 * shape and its links working while the API is away.
 */
/** A rail's Top slot is empty: the pages render it only while it has a title. */
const blankTop = (item: { title: string }): void => {
    item.title = '';
};

function clearStandIns(): void {
    const emptyList = (list: unknown[]): void => {
        list.length = 0;
    };

    emptyList(GALLERY);
    emptyList(ARTICLES);
    emptyList(RECENT_RAIL);
    blankTop(TOP_ARTICLE);
    emptyList(STORIES);
    emptyList(RECENT_STORIES);
    blankTop(TOP_STORY);
    emptyList(TEAM);
    emptyList(TESTIMONIALS);
    emptyList(PARTNERS);
    for (const collection of DOC_COLLECTIONS) {
        emptyList(DOCUMENTS[collection]);
        emptyList(DOC_RECENT[collection]);
        blankTop(DOC_TOP[collection]);
    }
}

async function hydrate(): Promise<boolean> {
    // One request, not fifteen: opening a connection per feed was the slowest
    // part of the first load, and the reason a page could paint before its
    // content arrived. An API without the combined feed falls back to the old way.
    const all = (await attempt<Bootstrap>('/site/bootstrap')) ?? (await eachFeed());
    const rail = (name: string): Rail | null => all?.resources?.[name] ?? null;

    const nav = all?.navigation ?? null;
    const projects = all?.projects ?? null;
    const team = all?.team ?? null;
    const testimonials = all?.testimonials ?? null;
    const partners = all?.partners ?? null;
    const articles = rail('articles');
    const stories = rail('stories');
    const gallery = rail('gallery');
    const socialPosts = rail('social-posts');
    const help = all?.help ?? null;
    const home = all?.home ?? null;
    const about = all?.about ?? null;
    const jdi = all?.jdi ?? null;
    const documents = DOC_COLLECTIONS.map((c) => rail(c));

    const cmsAnswered =
        !!nav ||
        !!projects ||
        !!articles ||
        !!stories ||
        !!gallery ||
        !!help ||
        !!home ||
        !!about ||
        documents.some(Boolean);

    SITE_STATE.cmsAnswered = cmsAnswered;
    if (cmsAnswered) {
        /* Content pools first: focus areas and projects resolve their attachments
           against the indices these produce. */

        if (articles) {
            fill(ARTICLES, articles.items, articleFrom);
            indexBy(articles.items, 'article:', refs.articles);
            Object.assign(TOP_ARTICLE, articles.top ? articleFrom(articles.top) : emptyArticle());
            fill(RECENT_RAIL, articles.recent, articleFrom);
        }

        if (stories) {
            fill(STORIES, stories.items, storyFrom);
            indexBy(stories.items, 'story:', refs.stories);
            Object.assign(TOP_STORY, stories.top ? storyFrom(stories.top) : emptyArticle());
            fill(RECENT_STORIES, stories.recent, storyFrom);
        }

        if (socialPosts) {
            // The bundled pool is empty, so there is nothing to reconcile against:
            // whatever the CMS lists is the whole set.
            const NETWORKS: SocialNetwork[] = ['x', 'instagram', 'linkedin'];
            const next: SocialPost[] = docs(socialPosts.items)
                .map((doc) => ({
                    network: (NETWORKS.find((n) => n === doc.network) ?? 'x') as SocialNetwork,
                    url: str(doc.url),
                    embedUrl: str(doc.embedUrl),
                    caption: str(doc.caption),
                }))
                .filter((p) => p.embedUrl);
            SOCIAL_POSTS.length = 0;
            SOCIAL_POSTS.push(...next);
        }

        if (gallery) {
            reconcile(GALLERY, gallery.items, galleryFrom);
            indexBy(gallery.items, 'gallery:', refs.gallery);
        }

        DOC_COLLECTIONS.forEach((collection: DocCollection, i) => {
            const rail = documents[i];
            if (!rail) return;
            fill(DOCUMENTS[collection], rail.items, downloadFrom);
            indexBy(rail.items, `doc:${collection}:`, refs.documents);
            Object.assign(DOC_TOP[collection], rail.top ? docAsArticle(rail.top) : emptyArticle());
            fill(DOC_RECENT[collection], rail.recent, docAsArticle);
            if (collection === 'publications') mergeShowcase(rail.items);
        });

        if (nav) {
            mergeNavigation(nav);
            mergeFocusAreas(docs(nav.focusAreas));
            mergeInnovations(docs(nav.innovations));
        }
        if (projects) mergeProjects(projects.items);
        if (team) mergeTeam(team.items);
        if (testimonials) mergeTestimonials(docs(testimonials.items));
        if (partners) mergePartners(docs(partners.items));
        if (help) mergeHelp(help);
        if (home) mergeHome(obj(home.content));
        if (about) mergeAbout(obj(about.content));
        if (jdi) mergeJdi(jdi);
    }

    // Nothing answered — the API is unreachable. The page copy bundled with the
    // site is the organisation's own and stands, but the rows in the content
    // pools are invented stand-ins, and a visitor must never read those as real
    // work. Empty them, and let each section say it has nothing to show.
    if (!cmsAnswered) {
        SITE_STATE.hydrating = false;
        clearStandIns();
        publishContentVersion();
        return false;
    }

    // Whatever has already rendered was read from the bundled pools; tell it to look again.
    SITE_STATE.hydrating = false;
    publishContentVersion();
    return true;
}

/**
 * Runs once, before the app mounts. Capped so a slow or hanging API delays first
 * paint by at most a moment — after that the bundled content renders, and the
 * feeds that land late bump `siteContentVersion`, which re-renders the tree
 * against the real content. The cap bounds how long the screen stays blank; it
 * no longer decides whether the CMS is shown at all.
 */
export function hydrateSiteContent(timeoutMs = 2500): Promise<boolean> {
    return Promise.race([
        hydrate(),
        new Promise<boolean>((resolve) => setTimeout(() => resolve(false), timeoutMs)),
    ]).catch(() => false);
}
